import FootballStatsCategory from "../../../../stats/football-stats-category";
import FootballState from "../../../../state/football-state";
import StatsRule from "../../../../../common/stats-rule";

/**
 * Stats rule for recording Rebound Net return stats (Indoor Football).
 *
 * Like a kickoff return, return yards are measured from where the ball was
 * kicked (the KickOff fragment's `kickedTo`) to where the returner was downed.
 * The down spot lives on the outcome fragment — `caughtAt` for the caught
 * states, `fieldedAt` for the fielded states (captured by the wizard via
 * `requiredAfterNextState: [Tackle, OutOfBounds]`). The Tackle fragment carries
 * no yardline.
 *
 * Per arena rules a rebound off a PASS is a reception/interception rather than
 * a kick return, so only kickoff-originated plays have a kick reference; rebound
 * nets off a pass/PAT record a 0-yard return attempt here (reception/interception
 * classification is handled elsewhere).
 */
export default class ReboundNetReturnStats extends StatsRule {
    // eslint-disable-next-line no-unused-vars
    applicable(
        gameContext,
        ruleContext,
        eventConfiguration,
        { play, fragment }
    ) {
        const isReboundNetOutcome = [
            FootballState.ReboundNetCaughtOffense,
            FootballState.ReboundNetCaughtDefense,
            FootballState.ReboundNetFieldedKicking,
            FootballState.ReboundNetFieldedReceiving
        ].includes(fragment.associatedState);

        return (
            isReboundNetOutcome &&
            // Catch outcomes are shared with the Bar Ball (Hit Goalpost) flow;
            // only record a Rebound Net return when the ball actually hit the net.
            play.historyContains(FootballState.HitReboundNet) &&
            super.applicable(gameContext, ruleContext, eventConfiguration, {
                play,
                fragment
            })
        );
    }

    // eslint-disable-next-line no-unused-vars
    async apply(
        gameContext,
        ruleContext,
        eventConfiguration,
        { play, fragment }
    ) {
        // caughtBy/caughtAt for caught states; fieldedBy/fieldedAt for fielded
        // states. The spot is where the returner was downed.
        const returnedBy = fragment.caughtBy || fragment.fieldedBy;
        const downSpot =
            fragment.caughtAt != null ? fragment.caughtAt : fragment.fieldedAt;

        if (!returnedBy || downSpot == null) {
            return [];
        }

        // Return is measured from where the ball was kicked. Rebound nets off a
        // pass/PAT have no kickoff reference and record a 0-yard attempt.
        const kickOff = play.getFirstFragment(FootballState.KickOff);
        const kickedTo = kickOff?.kickedTo;

        const returnerTeamIsHome = returnedBy.teamId === gameContext.homeTeamId;
        const directionMultiplier = returnerTeamIsHome ? 1 : -1;
        const yardsReturned =
            kickedTo == null ? 0 : (downSpot - kickedTo) * directionMultiplier;

        // Check for touchdown
        const fieldLength = gameContext.getFieldLength();
        const resultedInTd = returnerTeamIsHome
            ? downSpot >= fieldLength
            : downSpot <= 0;

        return this.addStats({
            gameId: play.gameId,
            playId: play.id,
            period: gameContext.currentPeriod,
            teamId: returnedBy.teamId,
            playerId: returnedBy.id,
            driveId: gameContext.currentDriveId,
            fragmentId: fragment.id,
            category: FootballStatsCategory.REBOUND_NET_RETURN,
            values: {
                yards: yardsReturned,
                completed: 1,
                attempted: 1,
                touchdowns: resultedInTd ? 1 : 0
            }
        });
    }
}
